import { Batch } from '../types';
import { createCompressedCache } from './compressedCache';

const REFRESH_DELAY = 30 * 60 * 1000; // 30 minutes

// Strip a batch down to the fields the app actually renders before persisting.
const slimBatch = (batch: Batch): Partial<Batch> => ({
  _id: batch._id,
  name: batch.name,
  batchNo: batch.batchNo,
  status: batch.status,
  brewer: batch.brewer,
  measuredEfficiency: batch.measuredEfficiency,
  estimatedOg: batch.estimatedOg,
  estimatedFg: batch.estimatedFg,
  estimatedColor: batch.estimatedColor,
  estimatedIbu: batch.estimatedIbu,
  brewDate: batch.brewDate,
  bottlingDate: batch.bottlingDate,
  _timestamp_ms: batch._timestamp_ms, // CRITICAL: incremental sync compares this
  cost: batch.cost,
  recipe: {
    name: batch.recipe?.name,
    batchSize: batch.recipe?.batchSize,
    boilSize: batch.recipe?.boilSize,
    style: batch.recipe?.style,
    yeast: batch.recipe?.yeast,
    equipment: batch.recipe?.equipment,
    efficiency: batch.recipe?.efficiency,
    boilTime: batch.recipe?.boilTime,
    og: batch.recipe?.og,
    fg: batch.recipe?.fg,
    ibu: batch.recipe?.ibu,
    color: batch.recipe?.color,
    buGuRatio: batch.recipe?.buGuRatio,
    preBoilGravity: batch.recipe?.preBoilGravity,
    nutrition: batch.recipe?.nutrition,
    author: batch.recipe?.author,
    brewer: batch.brewer,
    data: batch.recipe?.data ? {
      mashWaterAmount: batch.recipe.data.mashWaterAmount,
      spargeWaterAmount: batch.recipe.data.spargeWaterAmount,
      totalWaterAmount: batch.recipe.data.totalWaterAmount,
      strikeTemp: batch.recipe.data.strikeTemp,
    } : undefined,
    mash: batch.recipe?.mash ? {
      name: batch.recipe.mash.name,
      steps: batch.recipe.mash.steps,
    } : undefined,
    hops: (batch.recipe?.hops || []).map(hop => ({
      _id: hop._id,
      name: hop.name,
      amount: hop.amount,
      use: hop.use,
      time: hop.time,
      timeUnit: hop.timeUnit,
      alpha: hop.alpha,
      ibu: hop.ibu,
    })),
  },
  batchFermentables: batch.batchFermentables || [],
  batchHops: (batch.batchHops || []).map((hop: any) => ({
    _id: hop._id,
    name: hop.name,
    amount: hop.amount,
    use: hop.use,
    time: hop.time,
    timeUnit: hop.timeUnit,
    alpha: hop.alpha,
    ibu: hop.ibu,
    totalCost: hop.totalCost,
  })),
  batchHopsLocal: (batch.batchHopsLocal || []).map((hop: any) => ({
    _id: hop._id,
    name: hop.name,
    amount: hop.amount,
    use: hop.use,
    time: hop.time,
    timeUnit: hop.timeUnit,
    alpha: hop.alpha,
    ibu: hop.ibu,
  })),
  batchYeasts: batch.batchYeasts || [],
  batchMiscs: batch.batchMiscs || [],
  measuredAbv: batch.measuredAbv,
  measuredAttenuation: batch.measuredAttenuation,
  measuredOg: batch.measuredOg,
  measuredFg: batch.measuredFg,
  estimatedBuGuRatio: batch.estimatedBuGuRatio,
  events: batch.events,
} as Partial<Batch>);

export const batchCache = createCompressedCache<Batch>({
  storageKey: 'brewfather_batches_cache',
  lastRefreshKey: 'brewfather_batches_cache_last_refresh',
  refreshDelayMs: REFRESH_DELAY,
  slim: slimBatch,
});

export const getCachedBatches = (): Batch[] | null => batchCache.get();
export const setCachedBatches = (batches: Batch[]): void => batchCache.set(batches);
export const clearBatchesCache = (): void => batchCache.clear();
export const shouldRefreshCache = (): boolean => batchCache.shouldRefresh();
export const getLastRefreshTime = (): Date | null => batchCache.getLastRefreshTime();

// Formatted "last synced" string for view headers
export const getFormattedLastRefreshTime = (): string | null => {
  const lastRefresh = getLastRefreshTime();
  if (!lastRefresh) return null;

  const diffMins = Math.round((Date.now() - lastRefresh.getTime()) / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;

  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return lastRefresh.toLocaleDateString();
};
